import { _activeHost } from './active-host'
import { Subject } from 'rxjs'

/** Deliberate, small navigation/identity projections; never a raw state dump. */
export type ContextValue = null | boolean | number | string | ContextValue[] | { [key: string]: ContextValue }

export type ContextSnapshot =
	| { status: 'ready'; value: ContextValue }
	| { status: 'loading' | 'unavailable' | 'error'; reason?: string }

export interface ContextScope {
	/** The app host whose state is being described, rather than the inspecting control. */
	host?: HTMLElement
}

export interface ContextProvider {
	capture(scope: ContextScope): ContextSnapshot
	/** Owner-controlled selection through the domain's normal authorization path. */
	restore?(value: ContextValue, scope: ContextScope): void | Promise<void>
}

const CONTEXTS_KEY = Symbol.for('schmancy.state.contexts')
const slot = globalThis as { [CONTEXTS_KEY]?: Map<string, { provider: ContextProvider }> }
const contexts = (slot[CONTEXTS_KEY] ??= new Map<string, { provider: ContextProvider }>())
const CONTEXT_CHANGES_KEY = Symbol.for('schmancy.state.contextChanges')
const changesSlot = globalThis as { [CONTEXT_CHANGES_KEY]?: Subject<void> }
const changes = (changesSlot[CONTEXT_CHANGES_KEY] ??= new Subject<void>())

/** Registration changes wake consumers waiting for lazily mounted context owners. */
export const contextChanges$ = changes.asObservable()

/** Re-registration replaces an HMR owner; an older owner's disposer cannot erase it. */
export function registerContext(namespace: string, provider: ContextProvider): () => void {
	if (!namespace.trim() || namespace.length > 128 || [...namespace].some(character => character.charCodeAt(0) < 32)) {
		throw new Error('Context namespace must be a nonempty name of at most 128 characters')
	}
	if (!contexts.has(namespace) && contexts.size >= 64) throw new Error('Context provider limit exceeded')
	const registration = { provider }
	contexts.set(namespace, registration)
	changes.next()
	return () => {
		if (contexts.get(namespace) !== registration) return
		contexts.delete(namespace)
		changes.next()
	}
}

// Canonical projection both rejects lossy JSON (NaN, opaque objects, accessors,
// cycles) and makes exact readback independent of object insertion order.
function project(value: unknown, depth = 0, ancestors = new Set<object>()): ContextValue {
	if (depth > 16) throw new Error('Context depth exceeded')
	if (value === null) return null
	if (typeof value === 'string' || typeof value === 'boolean') return value
	if (typeof value === 'number' && Number.isFinite(value)) return value
	if (typeof value !== 'object' || value === null || ancestors.has(value)) throw new Error('Context is not JSON-safe')
	if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) {
		throw new Error('Context must contain plain JSON values')
	}
	ancestors.add(value)
	try {
		if (Array.isArray(value)) {
			if (value.length > 16_384) throw new Error('Context size exceeded')
			const result: ContextValue[] = []
			for (let i = 0; i < value.length; i++) {
				const descriptor = Object.getOwnPropertyDescriptor(value, i)
				if (!descriptor || !('value' in descriptor)) throw new Error('Context array accessors and holes are unsupported')
				result.push(project(descriptor.value, depth + 1, ancestors))
			}
			return result
		}
		const result: { [key: string]: ContextValue } = Object.create(null)
		for (const key of Object.keys(value).sort()) {
			const descriptor = Object.getOwnPropertyDescriptor(value, key)
			if (!descriptor || !('value' in descriptor)) throw new Error('Context accessors are unsupported')
			result[key] = project(descriptor.value, depth + 1, ancestors)
		}
		return result
	} finally {
		ancestors.delete(value)
	}
}

function bounded(value: unknown): ContextValue {
	const result = project(value)
	if (new TextEncoder().encode(JSON.stringify(result)).length > 16_384) throw new Error('Context size exceeded')
	return result
}

/** Missing, loading, signed-out (ready null), and rejected reads remain distinct. */
export function captureContext(namespace: string, scope: ContextScope = {}): ContextSnapshot {
	const provider = contexts.get(namespace)?.provider
	if (!provider) return { status: 'unavailable', reason: 'Context provider is not registered' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
	try {
		const source = { host: scope.host ?? globalThis.document?.documentElement }
		const snapshot = _activeHost.run(source.host, () => provider.capture(source))
		if (snapshot.status === 'ready') return { status: 'ready', value: bounded(snapshot.value) }
		if (!['loading', 'unavailable', 'error'].includes(snapshot.status)) throw new Error('Invalid context status')
		return snapshot.reason === undefined
			? { status: snapshot.status }
			: { status: snapshot.status, reason: snapshot.reason.slice(0, 512) }
	} catch {
		return { status: 'error', reason: 'Context capture failed or exceeded its JSON projection bounds' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
	}
}

/** Discover all registered subject projections automatically. */
export function captureContexts(scope: ContextScope = {}): Record<string, ContextSnapshot> {
	const result: Record<string, ContextSnapshot> = Object.create(null)
	for (const namespace of [...contexts.keys()].sort()) result[namespace] = captureContext(namespace, scope)
	return result
}

/** A restored context is accepted only after its owner reports the exact selected value. */
export async function restoreContext(namespace: string, value: ContextValue, scope: ContextScope = {}): Promise<ContextSnapshot> {
	const registration = contexts.get(namespace)
	if (!registration) return { status: 'unavailable', reason: 'Context provider is not registered' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
	const { provider } = registration
	try {
		const expected = bounded(value)
		const source = { host: scope.host ?? globalThis.document?.documentElement }
		if (!provider.restore) {
			const actual = captureContext(namespace, source)
			if (actual.status === 'ready' && JSON.stringify(actual.value) === JSON.stringify(expected)) return actual
			return { status: 'unavailable', reason: 'Context provider is read-only and its current value does not match' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
		}
		await _activeHost.run(source.host, () => provider.restore?.(expected, source))
		if (contexts.get(namespace) !== registration) return { status: 'error', reason: 'Context provider changed during restoration' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
		const actual = captureContext(namespace, source)
		if (actual.status !== 'ready') return actual
		return JSON.stringify(actual.value) === JSON.stringify(expected)
			? actual
			: { status: 'error', reason: 'Restored context does not match the requested selection' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
	} catch {
		return { status: 'error', reason: 'Context restoration was refused or failed' } // schmancy-text-literal-ok: Context inspection/restoration diagnostic payload for tooling, never component UI wording.
	}
}
